import React, { useEffect, useState } from 'react';
import {
  Alert, Box, Button, CircularProgress, Dialog, DialogContent, DialogTitle, Divider, Paper, Typography
} from '@mui/material';
import { OpenInNew as OpenInNewIcon } from '@mui/icons-material';
import { authGet } from '../../services/apiService';
import { absoluteHttpUrl } from './assigneeDisplay';
import { formatSfTableDate } from './sfTableFormatting';
import { DEEP_GREEN, INK, MUTED, NEUTRAL_SURFACE } from './colorTokens';

/** INC / RITM / REQ ticket numbers: the only ServiceNow ids the stored details can be looked up by. */
export const SNOW_NUMBER = /^(INC|RITM|REQ)\d+$/i;

const PANEL_SX ={ p: 2, border: '1px solid rgba(26,46,34,0.1)', borderRadius: 1.5 };

const SnapshotRow = ({ label, children }) => {
  if (children == null || children === '' || children === false) return null;
  return (
    <>
      <Typography sx={{ color: MUTED, fontSize: '0.75rem' }}>{label}</Typography>
      <Typography component="div" sx={{ fontWeight: 600, fontSize: '0.8rem', color: INK, wordBreak: 'break-word' }}>
        {children}
      </Typography>
    </>
  );
};

const dateOrNull = (value) => (value ? formatSfTableDate(value) : null);

/** ServiceNow ticket (INC / RITM / REQ) details, opened from a Ticket Journey box or a ServiceNow case number. Stored data from the L1 sync. */
const SnowTicketDetailsDialog = ({ number, fallbackUrl, color, onClose }) => {
  const [state, setState] = useState({ loading: true, ticket: null, error: '' });

  useEffect(() => {
    let cancelled = false;
    setState({ loading: true, ticket: null, error: '' });
    authGet(`/api/customer-issues/snow-details/${encodeURIComponent(number)}`)
      .then((res) => {
        if (cancelled) return;
        if (res && res.success && res.ticket) setState({ loading: false, ticket: res.ticket, error: '' });
        else setState({ loading: false, ticket: null, error: (res && res.message) || 'ServiceNow ticket not found.' });
      })
      .catch((err) => {
        if (!cancelled) setState({ loading: false, ticket: null, error: (err && err.message) || 'Failed to load ServiceNow ticket.' });
      });
    return () => { cancelled = true; };
  }, [number]);

  const { ticket } = state;
  const url = absoluteHttpUrl((ticket && ticket.url) || fallbackUrl);

  return (
    <Dialog open onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: '1rem', fontWeight: 700 }}>
        <span>
          {ticket && ticket.type ? `${ticket.type} · ` : 'ServiceNow · '}
          <Box component="span" sx={{ color }}>{number}</Box>
        </span>
        <Button size="small" onClick={onClose} sx={{ textTransform: 'none', fontWeight: 600, color: MUTED }}>Close</Button>
      </DialogTitle>
      <DialogContent dividers sx={{ bgcolor: NEUTRAL_SURFACE }}>
        {state.loading && (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5, py: 6 }}>
            <CircularProgress size={28} />
            <Typography sx={{ fontSize: '0.85rem', color: MUTED }}>
              Retrieving {number} from the ServiceNow tickets stored by the L1 sync…
            </Typography>
          </Box>
        )}
        {state.error && <Alert severity="error">{state.error}</Alert>}
        {ticket && (
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1.2fr 1fr' }, gap: 2 }}>
            <Paper elevation={0} sx={{ ...PANEL_SX, minWidth: 0 }}>
              <Typography sx={{ fontWeight: 700, color: DEEP_GREEN, fontSize: '0.95rem', mb: 0.5 }}>{ticket.title || 'No short description'}</Typography>
              <Divider sx={{ my: 1.25 }} />
              <Typography sx={{ color: MUTED, fontSize: '0.75rem', mb: 0.5 }}>Description</Typography>
              <Typography sx={{ fontSize: '0.85rem', color: INK, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                {ticket.description || 'No description on this ticket.'}
              </Typography>
              {ticket.comments && (
                <>
                  <Divider sx={{ my: 1.5 }} />
                  <Typography sx={{ color: MUTED, fontSize: '0.75rem', mb: 0.5 }}>Comments</Typography>
                  <Typography sx={{ fontSize: '0.85rem', color: INK, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                    {ticket.comments.trim()}
                  </Typography>
                </>
              )}
            </Paper>

            <Paper elevation={0} sx={{ ...PANEL_SX, minWidth: 0, alignSelf: 'start' }}>
              <Typography sx={{ fontWeight: 700, color: DEEP_GREEN, fontSize: '0.95rem', mb: 1.25 }}>Ticket snapshot</Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '8px 12px', alignItems: 'start' }}>
                <SnapshotRow label="State">{ticket.state}</SnapshotRow>
                <SnapshotRow label="Type">{ticket.type}</SnapshotRow>
                <SnapshotRow label="Priority">{ticket.priority}</SnapshotRow>
                <SnapshotRow label="Urgency">{ticket.urgency}</SnapshotRow>
                <SnapshotRow label="Assigned to">{ticket.assignedTo || 'Unassigned'}</SnapshotRow>
                <SnapshotRow label="Caller">{ticket.caller}</SnapshotRow>
                <SnapshotRow label="Category">{ticket.category}</SnapshotRow>
                <SnapshotRow label="Sub-category">{ticket.subcategory}</SnapshotRow>
                <SnapshotRow label="Service">{ticket.service}</SnapshotRow>
                <SnapshotRow label="Opened">{dateOrNull(ticket.openedAt)}</SnapshotRow>
                <SnapshotRow label="Closed">{dateOrNull(ticket.closedAt)}</SnapshotRow>
                <SnapshotRow label="Linked cases">{(ticket.caseIds || []).length ? ticket.caseIds.join(', ') : null}</SnapshotRow>
                <SnapshotRow label="Last synced">{dateOrNull(ticket.syncedAt)}</SnapshotRow>
              </Box>
              {url && (
                <Button
                  size="small"
                  variant="outlined"
                  endIcon={<OpenInNewIcon sx={{ fontSize: 14 }} />}
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{ mt: 2, textTransform: 'none', borderColor: 'rgba(26,46,34,0.25)', color: DEEP_GREEN, fontWeight: 600 }}
                >
                  Open in ServiceNow
                </Button>
              )}
            </Paper>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default SnowTicketDetailsDialog;
